import { cloneDeep } from 'lodash-es';
import { UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';
import { RendererType } from '../../../data-models/field-definition';
import { ISimpleChoice } from '../../../data-models/field2.data-model';
import { ISelectMeta } from '../../../data-models/field3.data-model';
import { FMT } from '../../../util/formatter-service';
import { StringTransform } from '../../../util/formatters/StringFormatters';
import { schemaToFormFields } from '../../../util/schema-utils';
import { IForm } from '../../../view-models/form.view-model';
import { DATE_FORMAT_OPTIONS, getDateFormatSample } from './date-format-options';
import { FormatterFormViewModel } from './formatter-form.view-model';
import { FormatterTypePill } from './FormatterTypePills';

const TRANSFORM_OPTIONS: ISimpleChoice<StringTransform>[] = Object.values(StringTransform).map((value) => ({
  value,
  displayName: value,
}));

const toChoices = (values: string[]): ISimpleChoice<string>[] =>
  values.map((value) => ({ value, displayName: value }));

// `currency` is a legacy type on existing rows: still editable, but not offered on create.
// `array` formatters have no form-editable config yet.
const TYPE_OPTIONS = toChoices(['number', 'string', 'date', 'boolean']);

const TIME_ZONE_OPTIONS: ISimpleChoice<string>[] = [
  { value: '', displayName: "Viewer's time zone" },
  { value: 'UTC', displayName: 'UTC' },
];
const CLOCK_OPTIONS: ISimpleChoice<string>[] = [
  { value: '', displayName: 'Locale default' },
  { value: '12', displayName: '12-hour' },
  { value: '24', displayName: '24-hour' },
];

function selectMeta(values: ISimpleChoice<string>[]): ISelectMeta<string> {
  return { multi: false, values, formatValueToLabel: true };
}

/**
 * Format choices carry their rendered sample as the secondary label, computed per render so a
 * viewer sees the shape in their own locale (`Jan 01, 2025` / `01 janv. 2025`).
 */
function dateFormatChoices(): ISimpleChoice<string>[] {
  return DATE_FORMAT_OPTIONS.map(({ id, label, group, config }) => ({
    value: id,
    displayName: label,
    secondaryLabel: getDateFormatSample(config),
    group,
  }));
}

/**
 * Formatter config page schema. The full field set covers every editable type;
 * `formatterFormTransformer` narrows the edit modal down to the fields relevant
 * to the row's type (number/currency formatters expose much more config than
 * string ones).
 */
export function formatterConfigFormSchema() {
  return yup.object({
    id: yup.string().required().label('Id').formMeta({
      renderer: RendererType.text,
      description: 'Unique identifier. Locked after creation.',
    }),
    displayName: yup
      .string()
      .label('Name')
      .formMeta({
        renderer: RendererType.text,
        rendererMeta: { maxLength: -1 },
      }),
    description: yup
      .string()
      .label('Description')
      .formMeta({
        renderer: RendererType.text,
        rendererMeta: { maxLength: -1 },
      }),
    type: yup
      .string()
      .required()
      .label('Type')
      .formMeta({
        renderer: RendererType.singleSelect,
        rendererMeta: selectMeta(TYPE_OPTIONS),
      }),
    example: yup.string().label('Example').formMeta({
      renderer: RendererType.text,
      disabled: true,
    }),
    minDecimals: yup
      .number()
      .min(0)
      .integer()
      .label('Min Decimals')
      .formMeta({
        renderer: RendererType.number,
        rendererMeta: { groupingSeparator: '' },
      }),
    maxDecimals: yup
      .number()
      .min(0)
      .integer()
      .label('Max Decimals')
      .formMeta({
        renderer: RendererType.number,
        rendererMeta: { groupingSeparator: '' },
      }),
    shortNotation: yup.boolean().label('Short Notation').formMeta({
      renderer: RendererType.boolean,
      description: 'Compact display for large values, e.g. $1.2M instead of $1,234,567',
    }),
    suffix: yup
      .string()
      .label('Suffix')
      .formMeta({
        renderer: RendererType.text,
        description: 'Appended to the formatted value, e.g. x or %',
        rendererMeta: { maxLength: -1 },
      }),
    noValue: yup
      .string()
      .label('Null Display')
      .formMeta({
        renderer: RendererType.text,
        description: 'Shown when the value is null or undefined',
        rendererMeta: { maxLength: -1 },
      }),
    zeroValue: yup
      .string()
      .label('Zero Display')
      .formMeta({
        renderer: RendererType.text,
        description: 'Shown when the value is 0',
        rendererMeta: { maxLength: -1 },
      }),
    negativeValue: yup
      .string()
      .label('Negative Display')
      .formMeta({
        renderer: RendererType.text,
        description: 'Shown for negative values instead of the formatted number',
        rendererMeta: { maxLength: -1 },
      }),
    invalidValue: yup
      .string()
      .label('Invalid Display')
      .formMeta({
        renderer: RendererType.text,
        description: 'Shown when the value is NaN or Infinity',
        rendererMeta: { maxLength: -1 },
      }),
    emptyValue: yup
      .string()
      .label('Empty Display')
      .formMeta({
        renderer: RendererType.text,
        description: 'Shown when the value is an empty string',
        rendererMeta: { maxLength: -1 },
      }),
    transforms: yup
      .array()
      .label('Transforms')
      .formMeta({
        renderer: RendererType.multiSelect,
        rendererMeta: {
          multi: true,
          values: TRANSFORM_OPTIONS,
          formatValueToLabel: true,
        } as ISelectMeta<StringTransform>,
      }),
    format: yup
      .string()
      .label('Format')
      .formMeta({
        renderer: RendererType.singleSelect,
        rendererMeta: { ...selectMeta(dateFormatChoices()), enableGrouping: true },
        disableClearable: true,
      }),
    timeZone: yup
      .string()
      .label('Time Zone')
      .formMeta({
        renderer: RendererType.singleSelect,
        rendererMeta: selectMeta(TIME_ZONE_OPTIONS),
        disableClearable: true,
      }),
    clock: yup
      .string()
      .label('Clock')
      .formMeta({
        renderer: RendererType.singleSelect,
        rendererMeta: selectMeta(CLOCK_OPTIONS),
        disableClearable: true,
        description: 'Only affects formatters that show a time',
      }),
    trueValue: yup
      .string()
      .label('True Display')
      .formMeta({
        renderer: RendererType.text,
        description: 'Shown when the value is true',
        rendererMeta: { maxLength: -1 },
      }),
    falseValue: yup
      .string()
      .label('False Display')
      .formMeta({
        renderer: RendererType.text,
        description: 'Shown when the value is false',
        rendererMeta: { maxLength: -1 },
      }),
  });
}

/**
 * The id becomes the REST path segment and the registration key, so it is restricted
 * to safe characters. Base formatter ids are not yet seeded into the tenant table,
 * so the backend's create-409 cannot catch a collision with a built-in id — checking
 * the live registry prevents silently shadowing a system formatter tenant-wide.
 */
export function validateNewFormatterId(id: string): true | string {
  if (!id) {
    return true; // empty is the required rule's concern
  }
  if (!/^[a-zA-Z0-9_-]+$/.test(id)) {
    return 'Id may only contain letters, numbers, dashes and underscores';
  }
  if (Array.from(FMT.get().getAllFormatterIds()).includes(id)) {
    return 'A formatter with this id already exists';
  }
  return true;
}

// 'example' is intentionally absent: it is a computed list column (see
// getFormatterRowFields), not an editable field. The order here is the modal's field order for
// every type at once — the transformer only ever removes fields from it.
export function getFormatterFields() {
  return schemaToFormFields(formatterConfigFormSchema(), [
    'displayName',
    'id',
    'description',
    'type',
    'format',
    'timeZone',
    'clock',
    'minDecimals',
    'maxDecimals',
    'shortNotation',
    'suffix',
    'trueValue',
    'falseValue',
    'noValue',
    'zeroValue',
    'negativeValue',
    'invalidValue',
    'emptyValue',
    'transforms',
  ]);
}

// The 'all' view shows only the config features common to every formatter type;
// the per-family views add that family's own config columns (type is the
// discriminator, so it is redundant outside 'all').
const ROW_FIELDS_BY_PILL = {
  all: ['displayName', 'id', 'type', 'description', 'example', 'noValue'],
  numeric: [
    'displayName',
    'id',
    'description',
    'example',
    'minDecimals',
    'maxDecimals',
    'shortNotation',
    'suffix',
    'noValue',
    'zeroValue',
    'negativeValue',
    'invalidValue',
  ],
  text: ['displayName', 'id', 'description', 'example', 'noValue', 'emptyValue', 'transforms'],
  // No Format column: Example already shows the shape, and a row whose shape is not on the
  // curated list has nothing readable to put there (see CURRENT_DATE_FORMAT_ID).
  date: ['displayName', 'id', 'description', 'example', 'timeZone', 'noValue'],
  boolean: ['displayName', 'id', 'description', 'example', 'trueValue', 'falseValue', 'noValue'],
} as const;

export function getFormatterRowFields(pill: FormatterTypePill) {
  return schemaToFormFields(formatterConfigFormSchema(), [...ROW_FIELDS_BY_PILL[pill]]);
}

const NUMBER_FIELDS = [
  'minDecimals',
  'maxDecimals',
  'shortNotation',
  'suffix',
  'noValue',
  'zeroValue',
  'negativeValue',
  'invalidValue',
];
const DATE_FIELDS = ['format', 'timeZone', 'clock', 'noValue', 'invalidValue'];
const CONFIG_FIELDS_BY_TYPE: Record<string, string[]> = {
  number: NUMBER_FIELDS,
  currency: NUMBER_FIELDS,
  string: ['noValue', 'emptyValue', 'transforms'],
  date: DATE_FIELDS,
  boolean: ['trueValue', 'falseValue', 'noValue'],
};
const ALL_CONFIG_FIELDS = [...new Set(Object.values(CONFIG_FIELDS_BY_TYPE).flat())];

/**
 * Narrows the edit modal to the fields relevant to the row's type — a type with no editable
 * config (array) keeps only the common fields. `example` is a list column only.
 * Pattern mirrors `columnFormTransformer` in ../Columns/ColumnFields.ts.
 */
export function formatterFormTransformer(
  form: IForm<FormatterFormViewModel>,
  methods: UseFormReturn<FormatterFormViewModel>
): IForm<FormatterFormViewModel> {
  const dynamicForm = cloneDeep(form);
  const type = methods.watch('type') ?? form.data?.type;
  // The static form data carries an id only when editing an existing row; the id
  // is the identity (and the REST path), so it is locked after creation.
  const isEdit = Boolean(form.data?.id);

  ['id', 'type'].forEach((key) => {
    const field = dynamicForm.fields.get(key);
    if (field) {
      dynamicForm.fields.set(key, { ...field, disabled: isEdit });
    }
  });
  const idField = dynamicForm.fields.get('id');
  if (idField && !isEdit) {
    dynamicForm.fields.set('id', { ...idField, rules: { validate: validateNewFormatterId } });
  }
  // `currency` and `array` rows exist but are not offered on create, so their type would render
  // as an empty (disabled) select without this.
  pinUnlistedChoice(dynamicForm, 'type', form.data?.type);

  const visibleFields = new Set(CONFIG_FIELDS_BY_TYPE[type ?? ''] ?? []);
  if (type === 'date') {
    pinUnlistedChoice(dynamicForm, 'format', form.data?.format, {
      displayName: 'Current format',
      secondaryLabel: form.data?.example,
      group: 'Current format',
    });
    pinUnlistedChoice(dynamicForm, 'timeZone', form.data?.timeZone);
  }
  ALL_CONFIG_FIELDS.forEach((key) => {
    if (!visibleFields.has(key)) {
      dynamicForm.fields.delete(key);
    }
  });

  return dynamicForm;
}

/**
 * Appends the row's stored value to a select whose curated options do not cover it — a date shape
 * outside {@link DATE_FORMAT_OPTIONS}, or an IANA zone beyond the two offered. Without this the
 * field would render blank and editing any *other* field would rewrite the stored config to
 * whatever the user then picked. Pinning keeps the truth visible and restorable.
 */
function pinUnlistedChoice(
  form: IForm<FormatterFormViewModel>,
  key: string,
  value: string | undefined,
  choice: Partial<ISimpleChoice<string>> = {}
) {
  const field = form.fields.get(key);
  if (!value || !field) {
    return;
  }
  const rendererMeta = field.rendererMeta as ISelectMeta<string>;
  if (rendererMeta.values.some((option) => option.value === value)) {
    return;
  }
  form.fields.set(key, {
    ...field,
    rendererMeta: {
      ...rendererMeta,
      values: [...rendererMeta.values, { value, displayName: value, ...choice }],
    },
  });
}
